import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ImgHTMLAttributes,
  type ReactEventHandler,
} from "react";

export interface LazyImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  /**
   * Set for above-the-fold images (the LCP candidate). Native lazy loading on
   * a hero image delays the largest paint, so those opt out.
   */
  priority?: boolean;
}

const LazyImage = ({
  src,
  alt,
  priority = false,
  style,
  onLoad,
  onError,
  ...props
}: LazyImageProps) => {
  // React 18 does not map the camelCase `fetchPriority` prop onto the DOM
  // attribute, so pass the lowercase attribute through directly.
  const priorityAttrs = priority ? { fetchpriority: "high" } : {};

  const imgRef = useRef<HTMLImageElement>(null);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    setSettled(false);
    // A cached image can finish decoding before React attaches onLoad, which
    // previously left it stuck at opacity 0 forever.
    if (imgRef.current?.complete) setSettled(true);
  }, [src]);

  const handleLoad = useCallback<ReactEventHandler<HTMLImageElement>>(
    (event) => {
      setSettled(true);
      onLoad?.(event);
    },
    [onLoad]
  );

  // Reveal on error too, otherwise a broken image is an invisible dead space.
  const handleError = useCallback<ReactEventHandler<HTMLImageElement>>(
    (event) => {
      setSettled(true);
      onError?.(event);
    },
    [onError]
  );

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      {...priorityAttrs}
      onLoad={handleLoad}
      onError={handleError}
      {...props}
      style={{ opacity: settled ? 1 : 0, transition: "opacity 0.5s", ...style }}
    />
  );
};

export default LazyImage;
